import { type PropsWithChildren, type ReactNode, useContext, useEffect, useState } from "react";
import { api, IAvailableProductsByBooking, Product } from "../../../logic/api";
import TreeViewNode from "../TreeViewNode";
import { PagedDataView } from "../../lists/PagedDataView/PagedDataView";
import { ITreeViewLoaderProps, TreeViewCommand, TreeViewContext } from "../TreeView";
import flex from "../../../styles/flex";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import { useDate } from "../../../utility/useDate";

interface IFlags {
  bookingId: number;
  filter: string;
  returnDate?: string;
}

export interface IProductsForBookingTreeViewLoaderProps extends ITreeViewLoaderProps {
  renderItem?: (item: Product, index: number) => ReactNode;
}

const ProductsForBookingTreeViewLoader = (props: PropsWithChildren<IProductsForBookingTreeViewLoaderProps>) => {
  const flags: IFlags = (props.flags || {}) as IFlags;

  const [params, setParams] = useState<IAvailableProductsByBooking>({
    bookingId: flags.bookingId,
    filter: flags.filter,
    parentId: props.parentId,
  });

  useEffect(() => {
    setParams({ ...params, ...flags });
  }, [flags.filter]);

  const tvc = useContext(TreeViewContext);
  const isCapProductsSoldPerReturnDateReleased = useReleaseManagement(ReleaseFlag.releaseCapProductsSoldPerReturnDate);
  const { toFormattedDateString } = useDate();

  useEffect(() => {
    if (!tvc.state?.command) {
      return;
    }

    if (
      tvc.state.command.command === TreeViewCommand.reloadAll &&
      props.parentId !== tvc.state.treeRootId
    ) {
      return;
    }

    if (
      tvc.state.command.command === TreeViewCommand.reload &&
      props.parentId !== tvc.state.command.targetId
    ) {
      return;
    }

    setParams({ ...params });
    if (tvc.setState) {
      tvc.setState({
        ...tvc.state,
        command: undefined
      });
    }
  }, [tvc.state?.command]);

  const renderItem = (item: Product) => {
    const children = props.renderItem
      ? props.renderItem(item, 0)
      : "";

    if (isCapProductsSoldPerReturnDateReleased) {
      const isOverCapacity = (
        item.maxQtyPerDayEnabled == true &&
        item.totalPerDay != null &&
        item.soldPerDay != null &&
        ((item.totalPerDay == 0 && item.soldPerDay == 0) == false) &&
        (item.soldPerDay ?? 0) >= (item.totalPerDay ?? 0)
      );

      return <TreeViewNode record={item} disabled={isOverCapacity} >
        <div style={{ ...flex.flexRows, width: 300, maxWidth: 300 }}>
          <div style={flex.flexFill}>{children}</div>
          {(item.totalPerDay != null && item.soldPerDay != null && ((item.totalPerDay == 0 && item.soldPerDay == 0) == false) && item.maxQtyPerDayEnabled == true) && (
            <div style={{ textAlign: "right", width: 150, fontWeight: "bold", color: "red" }}>
              <small>{item.soldPerDay ?? 0} of {item.totalPerDay ?? 0} sold for {flags.returnDate ? toFormattedDateString(flags.returnDate) : ""}</small>
            </div>
          )}
        </div>
      </TreeViewNode>;
    }

    return <TreeViewNode record={item}>
      <div style={{ ...flex.flexRows, width: 300, maxWidth: 300 }}>
        <div style={flex.flexFill}>{children}</div>
        {
          (item.totalPerDay != null && item.soldPerDay != null && ((item.totalPerDay == 0 && item.soldPerDay == 0) == false) && item.maxQtyPerDayEnabled == true) &&
          (<div style={{ textAlign: "right", width: 150, fontWeight: "bold", color: (item.soldPerDay ?? 0) < (item.totalPerDay ?? 0) ? "green" : "red" }}><small>{item.soldPerDay ?? 0} of {item.totalPerDay ?? 0} sold</small></div>)
        }
      </div>

    </TreeViewNode>;
  }

  return <PagedDataView<IAvailableProductsByBooking, Product>
    apiHook={api.hooks.products.availableProductsByBooking}
    parameters={params}
    renderItem={renderItem}
  ></PagedDataView>
}

export default ProductsForBookingTreeViewLoader;
